import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "@mui/material";
import { useRouter } from "next/router";
import { useStore } from "outstated";
import { AppStore } from "pages/_app.js";
import dayjs from "dayjs";

// mui
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Skeleton from "@mui/material/Skeleton";
import Alert from "@mui/material/Alert";

import InputAdornment from "@mui/material/InputAdornment";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ButtonBase from "@mui/material/ButtonBase";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// lib
import formatMoney from "lib/formatMoney";
import scroller from "lib/scroller";
import sleep from "lib/sleep";
// components
import SpreadLTVTable from "components/Calculators/DSCRCalculator/components/SpreadLTVTable";
import CalculatorQuestions from "components/Calculators/ClosingCostCalculator/components/CalculatorQuestions";
import getDSCRLTVTiers from "components/Loans/utils/getDSCRLTVTiers";

import loadCalculation from "components/Calculators/components/utils/loadCalculation";
import createCalc from "components/Calculators/DSCRCalculator/utils/createCalc";
import updateCalc from "components/Calculators/DSCRCalculator/utils/updateCalc";
import * as gtag from "components/GTagManager";
import dynamic from "next/dynamic";
const AddressAutocomplete = dynamic(() =>
  import("components/AddressAutocomplete")
);
import SaveSharePDF from "components/Calculators/components/SaveSharePDF";

import QuestionRenderer from "components/Calculators/components/QuestionRenderer";
import Authenticator from "components/Authenticator";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";

const calcType = "Closing cost";

const loanTypeLineItemPresets = {
  "Conventional - 30 year mortgage": {
    "Origination fee": (loanAmount) => loanAmount * 0.02,
    "Loan fees": (loanAmount) => loanAmount * 0.01,
    Appraisal: (loanAmount) => 700,
    "Title fees": (loanAmount) => loanAmount * 0.0125,
    Escrow: (loanAmount) => loanAmount * 0.005,
    "Prepaid interest": (loanAmount) => loanAmount * 0.0025,
    "Prepaid insurance": (loanAmount) => loanAmount * 0.0025,
  },
  "Non-QM - primary residence": {
    "Origination fee": (loanAmount) => loanAmount * 0.01,
    "Loan fees": (loanAmount) => loanAmount * 0.01,
    Appraisal: (loanAmount) => 700,
    "Title fees": (loanAmount) => loanAmount * 0.0125,
    Escrow: (loanAmount) => loanAmount * 0.005,
    "Prepaid interest": (loanAmount) => loanAmount * 0.0025,
    "Prepaid insurance": (loanAmount) => loanAmount * 0.0025,
  },
  DSCR: {
    "Origination fee": (loanAmount) => loanAmount * 0.01,
    "Loan fees": (loanAmount) => 1995,
    Appraisal: (loanAmount) => 700,
    "Title fees": (loanAmount) => loanAmount * 0.0125,
    Escrow: (loanAmount) => loanAmount * 0.005,
    "Prepaid interest": (loanAmount) => loanAmount * 0.0025,
    "Prepaid insurance": (loanAmount) => loanAmount * 0.0025,
  },
  "Fix and rent": {
    "Origination fee": (loanAmount) => loanAmount * 0.02,
    "Loan fees": (loanAmount) => 1995,
    Appraisal: (loanAmount) => 700,
    "Title fees": (loanAmount) => loanAmount * 0.0125,
    Escrow: (loanAmount) => loanAmount * 0.005,
    "Prepaid interest": (loanAmount) => loanAmount * 0.005,
    "Prepaid insurance": (loanAmount) => loanAmount * 0.01,
  },
  "Build to rent": {
    "Origination fee": (loanAmount) => loanAmount * 0.02,
    "Loan fees": (loanAmount) => 1995,
    Appraisal: (loanAmount) => 700,
    "Title fees": (loanAmount) => loanAmount * 0.0125,
    Escrow: (loanAmount) => loanAmount * 0.005,
    "Prepaid interest": (loanAmount) => loanAmount * 0.005,
    "Prepaid insurance": (loanAmount) => loanAmount * 0.01,
  },
};

export default function ClosingCostCalculator({
  externalCalc,
  loadCalcs,
  refCalcs,
  externalExampleCalc,
}) {
  const theme = useTheme();
  const router = useRouter();

  const [suggestionChoice, setSuggestionChoice] = useState(false);

  const {
    user,
    userProfile,
    setSnackbarData,
    showLogInDialog,
    setShowLogInDialog,
  } = useStore(AppStore);
  const [loanQuoteData, setLoanQuoteData] = useState({});
  const [resultsData, setResultsData] = useState([]);
  const [singleItemAnalysisData, setSingleItemAnalysisData] = useState({});

  const [showQuestionDescription, setShowQuestionDescription] = useState({
    "Monthly cashflow": true,
    "Closing cost": false, // Start collapsed
  });
  // Answers is what users give and shown in the UI
  const [answers, setAnswers] = useState([]);
  // Questions is what has blueprint of what we ask
  const [questions, setQuestions] = useState([]);
  // All current session answers are held in this
  const [currentValues, setCurrentValues] = useState({});
  const [loanAmount, setLoanAmount] = useState({});
  const [loanPayment, setLoanPayment] = useState(0);
  // Add state for tracking value changes
  const [valueChanges, setValueChanges] = useState({});
  // Add original value ref
  const originalValueRef = useRef({});

  const [dscr, setDSCR] = useState(0);
  const [closingCost, setClosingCost] = useState(0);
  const [monthlyCashflow, setMonthlyCashflow] = useState(0);
  const [showSaveFields, setShowSaveFields] = useState(false);
  const [showUpdateFields, setShowUpdateFields] = useState(false);
  const [calc, setCalc] = useState(externalCalc ? externalCalc : null);
  const [showCreatedSuccessMessage, setShowCreatedSuccessMessage] =
    useState(false);
  const [showMeMyRate, setShowMeMyRate] = useState(false);
  // DSCR
  const [creditScore, setCreditScore] = useState(null);
  const [showCopiedSuccess, setShowCopiedSuccess] = useState(false);
  const [anchorEl, setAnchorEl] = useState({});
  // Add this with other state variables at the top
  const [totalPayment, setTotalPayment] = useState(0);
  const [basePayment, setBasePayment] = useState(0);

  // special section that isn't part of original questions
  // but must be added later so we can still capture in 'answers'
  const firstPartOfQuestions = ["Loan amount", "Loan type"];

  // const secondPartOfQuestions = ["Property tax", "Insurance"];

  const thirdPartOfQuestions = [
    "Origination fee",
    "Loan fees",
    "Appraisal",
    "Title fees",
    "Escrow",
    "Prepaid interest",
    "Prepaid insurance",
  ];

  const fourthPart = ["Calculation name", "Address (optional)"];

  // Add this state at the top with other state declarations
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [showAllDescriptions, setShowAllDescriptions] = useState(false);
  const [isInView, setIsInView] = useState(false);
  // Add ref to track if expansion has been triggered
  const hasExpandedRef = useRef(false);

  // Update the scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (!hasExpandedRef.current) {
        const closingCostSection = document.getElementById("pdf-5");
        if (closingCostSection) {
          const rect = closingCostSection.getBoundingClientRect();
          const isLiftedOffBottom = rect.bottom < window.innerHeight;

          if (isLiftedOffBottom) {
            setShowQuestionDescription((prev) => ({
              ...prev,
              "Closing cost": true,
            }));
            hasExpandedRef.current = true; // Mark as expanded
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleOpen = (event, calc) => {
    const updatedAnchorEl = Object.assign({}, anchorEl);
    updatedAnchorEl[calc.id] = event.currentTarget;
    setAnchorEl(updatedAnchorEl);
  };

  const handleClose = (calc) => {
    if (calc) {
      const updatedAnchorEl = Object.assign({}, anchorEl);
      delete updatedAnchorEl[calc.id];
      setAnchorEl(updatedAnchorEl);
    }
  };

  // Custom hook to get the previous state
  const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };
  // Save the previous state
  const previousValues = usePrevious(currentValues);

  // TODO review necessity
  const defaultValues = {
    // "Amortization type": "Principal and interest",
    // "Payment frequency": "Monthly",
  };

  useEffect(() => {
    loadCalculation(
      defaultValues,
      externalCalc,
      externalExampleCalc,
      CalculatorQuestions,
      setQuestions,
      setAnswers,
      setCurrentValues,
      setShowUpdateFields
    );
  }, []);

  useEffect(() => {
    if (Object.keys(currentValues).length > 0) {
      // Check if Loan amount or Loan type has changed
      const loanAmountChanged =
        currentValues["Loan amount"] !== previousValues?.["Loan amount"];
      const loanTypeChanged =
        currentValues["Loan type"] !== previousValues?.["Loan type"];

      // Create updated current values object
      const updatedCurrentValues = { ...currentValues };

      if (loanAmountChanged || loanTypeChanged) {
        // Get loan type and amount
        const loanAmount = parseFloat(currentValues["Loan amount"]) || 0;
        const loanType =
          currentValues["Loan type"] || "Conventional - 30 year mortgage";

        // Check if we have presets for this loan type
        if (loanTypeLineItemPresets[loanType]) {
          // Get the presets for this loan type
          const presets = loanTypeLineItemPresets[loanType];

          // Calculate and store value changes
          const newValueChanges = {};
          Object.entries(presets).forEach(([key, calcFn]) => {
            const newValue = calcFn(loanAmount);
            const oldValue = parseFloat(currentValues[key]) || 0;
            const difference = newValue - oldValue;

            if (difference !== 0) {
              newValueChanges[key] = difference;
              updatedCurrentValues[key] = newValue;
            }
          });

          // Set value changes and clear after 2 seconds
          setValueChanges(newValueChanges);
          setTimeout(() => {
            setValueChanges({});
          }, 2000);
        }

        // Update current values with calculated amounts
        setCurrentValues(updatedCurrentValues);
      }

      // Get all fees
      let originationFee =
        parseFloat(updatedCurrentValues["Origination fee"]) || 0;
      let loanFees = parseFloat(updatedCurrentValues["Loan fees"]) || 0;
      let appraisal = parseFloat(updatedCurrentValues["Appraisal"]) || 0;
      let titleFees = parseFloat(updatedCurrentValues["Title fees"]) || 0;
      let escrowFees = parseFloat(updatedCurrentValues["Escrow"]) || 0;
      let prepaidInterest =
        parseFloat(updatedCurrentValues["Prepaid interest"]) || 0;
      let prepaidInsurance =
        parseFloat(updatedCurrentValues["Prepaid insurance"]) || 0;

      // Optional fields
      const calculationName = updatedCurrentValues["Calculation name"] || "";
      const address = updatedCurrentValues["Address (optional)"] || "";

      // Calculate total fees for closing costs
      const totalClosingCosts =
        originationFee +
        loanFees +
        appraisal +
        titleFees +
        escrowFees +
        prepaidInterest +
        prepaidInsurance;

      // Set cash to close
      setClosingCost(totalClosingCosts);
    }
  }, [currentValues, previousValues]);

  const handleUserSelection = (
    value,
    answer,
    answers,
    questions,
    setAnswers
  ) => {
    const questionsSlice = questions.slice();
    const updatedAnswers = answers.slice(0);
    const isValidAnswer = answer.q_validation_func(value, answers);

    if (isValidAnswer) {
      const targetAnswer = updatedAnswers.filter((updatedAnswer) => {
        return updatedAnswer.q_id === answer.q_id;
      })[0];

      // Calculate value change for currency fields
      if (answer.a_type === "currency") {
        // Store original value when user starts typing
        if (!originalValueRef.current[answer.q_title]) {
          originalValueRef.current[answer.q_title] =
            parseFloat(currentValues[answer.q_title]) || 0;
        }

        const newValue = parseFloat(value) || 0;
        const difference = newValue - originalValueRef.current[answer.q_title];

        if (difference !== 0) {
          setValueChanges((prev) => ({
            ...prev,
            [answer.q_title]: difference,
          }));

          // Clear the change and original value after 2 seconds
          setTimeout(() => {
            setValueChanges((prev) => {
              const newChanges = { ...prev };
              delete newChanges[answer.q_title];
              return newChanges;
            });
            delete originalValueRef.current[answer.q_title];
          }, 2000);
        }
      }

      // save users answer
      targetAnswer.a_string = value;
      // reset error just in case
      targetAnswer.a_error = false;

      // determine if need to insert follow up questions
      // const next_q_id = answer.q_next_step_func(targetAnswer, answers);
      const next_q_id = answer.q_next_step_func(answer, answers, questions);

      const q_ids = updatedAnswers.map((answer) => {
        return answer.q_id;
      });

      // NEXT QUESTION is always next in position
      // NOT like before when it was based on length of q_id
      // this was limiting in terms of folding questions of the tree back
      // and allowing the tree to merge back to left most path
      const posOfNextQuestion =
        updatedAnswers
          .map((updatedAnswer) => {
            return updatedAnswer.q_id;
          })
          .indexOf(answer.q_id) + 1;

      const nextPotentialQuestion = updatedAnswers[posOfNextQuestion];

      if (
        next_q_id &&
        nextPotentialQuestion &&
        nextPotentialQuestion?.q_id !== next_q_id
      ) {
        // OFF LEFT MOST PATH
        const newNextQuestion = questionsSlice.filter((question) => {
          return question.q_id === next_q_id;
        })[0];

        // LEAF, remove all subsequent questions from answers
        if (newNextQuestion.node_type === "leaf") {
          // if a LEAF never delete anything (like slice)
          // instead add hidden property
          for (let i = posOfNextQuestion; i < updatedAnswers.length; i++) {
            updatedAnswers[i].node_visibility = "hidden";
          }
        } else {
          // NODE, make sure subsequent questions are prents
          // need to make left most path starting from next questions's id.
          let leftMostPathFromCurrentNode = [];
          let deeperNextQuestion = newNextQuestion;
          let deeperNextQuestionChildrenLength =
            deeperNextQuestion.q_child_nodes.length;
          const map_q_id = questionsSlice.map((question) => {
            return question.q_id;
          });

          while (deeperNextQuestionChildrenLength !== 0) {
            deeperNextQuestion =
              questions[
                map_q_id.indexOf(
                  deeperNextQuestion.q_next_step_func(
                    deeperNextQuestion,
                    [],
                    []
                  )
                )
              ];

            if (deeperNextQuestion) {
              deeperNextQuestionChildrenLength =
                deeperNextQuestion.q_child_nodes.length;

              leftMostPathFromCurrentNode.push(deeperNextQuestion);
            } else {
              deeperNextQuestionChildrenLength = 0;
            }
          }

          const earlierQuestions = updatedAnswers.slice(
            0,
            posOfNextQuestion + 2
          );

          let counter = 0;
          for (
            let i = posOfNextQuestion + 1;
            i < leftMostPathFromCurrentNode.length + posOfNextQuestion + 1;
            i++
          ) {
            if (
              updatedAnswers[i]?.q_id === leftMostPathFromCurrentNode[counter]
            ) {
              //show the node
              // updatedAnswers[i].node_type = "node";
            } else {
              //or overwrite if different
              updatedAnswers[i] = leftMostPathFromCurrentNode[counter];
              // updatedAnswers[i].node_type = "node";
            }
            counter = counter + 1;
          }

          // make sure all nodes are re-updated
          // to have node_type be node so nothing is hidden
          // if people jump around
          for (let i = 0; i < updatedAnswers.length; i++) {
            if (updatedAnswers[i].node_type === "node") {
              updatedAnswers[i].node_visibility = "visible";
            }
          }
        }

        updatedAnswers[posOfNextQuestion] = questionsSlice.filter(
          (question) => {
            return question.q_id === next_q_id;
          }
        )[0];
        updatedAnswers[posOfNextQuestion].node_visibility = "visible";
      }

      setAnswers(updatedAnswers);
    } else {
      updatedAnswers.filter((updatedAnswer) => {
        return updatedAnswer.q_id === answer.q_id;
      })[0].a_error = true;

      setAnswers(updatedAnswers);
      return;
    }
  };

  const generateAnswerOptions = (answer, index, answers) => {
    let answerOptionsUI;

    switch (answer.a_type) {
      case "leaf":
        answerOptionsUI = (
          <Box key={`${index}_${answer.a_type}`} sx={{ mb: 2 }}>
            <div></div>
          </Box>
        );
        break;
      case "percent":
        let localValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string
              ? answer.a_string
              : null
            : !isNaN(currentValues[answer.q_title])
            ? currentValues[answer.q_title]
            : null;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              margin: theme.spacing(1),
              width: "100%",
              [theme.breakpoints.down("md")]: {
                padding: `0 !important`,
              },
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              sx={{
                width: "100%",
                [theme.breakpoints.down("md")]: {
                  padding: `0 !important`,
                },
              }}
              error={answer.a_error}
              InputLabelProps={{ shrink: localValue }}
              label={
                answer.a_error
                  ? "Error"
                  : answer.q_placeholder
                  ? answer.q_placeholder
                  : answer.q_title
              }
              type={answer.a_input_type}
              value={localValue || ""}
              onChange={(e) => {
                const num = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = num;

                setCurrentValues(updatedCurrentValues);

                handleUserSelection(
                  num,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              helperText={
                answer.a_error ? answer.q_invalid_answer_help_text : ""
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();

                  const next_q_id = answer.q_next_step_func(
                    answer,
                    answers,
                    questions
                  );

                  if (next_q_id) {
                    const nextQuestion = questions.find(
                      (q) => q.q_id === next_q_id
                    );

                    const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                    const nextQuestionTextField = document.getElementById(
                      nextQuestionTextFieldId
                    );

                    if (nextQuestionTextField) {
                      nextQuestionTextField.focus();
                    }
                  }
                }
              }}
            />
          </Box>
        );
        break;
      case "mc":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              flex: "0 1 100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {answer.a_opts(answers).map((a_opt, index) => {
              {
                return (
                  <Button
                    id={
                      index === 0
                        ? `text_field_${answer.q_title}_${
                            calc?.id ? calc.id : 0
                          }`
                        : `text_field_${answer.q_title}_opt_${index}`
                    }
                    sx={{ margin: theme.spacing(1) }}
                    variant={
                      currentValues?.[answer.q_title] === a_opt
                        ? "contained"
                        : "outlined"
                    }
                    onClick={(e) => {
                      const updatedCurrentValues = Object.assign(
                        {},
                        currentValues
                      );
                      updatedCurrentValues[answer.q_title] = a_opt;

                      setCurrentValues(updatedCurrentValues);

                      handleUserSelection(
                        a_opt,
                        answer,
                        answers,
                        questions,
                        setAnswers
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        const updatedCurrentValues = Object.assign(
                          {},
                          currentValues
                        );
                        updatedCurrentValues[answer.q_title] = a_opt;

                        setCurrentValues(updatedCurrentValues);

                        handleUserSelection(
                          a_opt,
                          answer,
                          answers,
                          questions,
                          setAnswers
                        );

                        const next_q_id = answer.q_next_step_func(
                          answer,
                          answers,
                          questions
                        );

                        if (next_q_id) {
                          const nextQuestion = questions.find(
                            (q) => q.q_id === next_q_id
                          );

                          const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                          const nextQuestionTextField = document.getElementById(
                            nextQuestionTextFieldId
                          );

                          if (nextQuestionTextField) {
                            nextQuestionTextField.focus();
                          }
                        }
                      }
                    }}
                  >
                    {a_opt}
                  </Button>
                );
              }
            })}
          </Box>
        );
        break;
      case "text":
        const localTextValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string
              ? answer.a_string
              : null
            : currentValues[answer.q_title];

        answerOptionsUI = (
          <Box key={`${index}_${answer.a_type}`}>
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              sx={{ margin: theme.spacing(5, 0) }}
              error={answer.a_error}
              label={
                answer.a_error
                  ? "Error"
                  : answer.q_placeholder
                  ? answer.q_placeholder
                  : answer.q_title
              }
              type={answer.a_input_type}
              value={localTextValue}
              InputLabelProps={{ shrink: localTextValue }}
              onChange={(e) => {
                const val = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = val;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  val,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              helperText={
                answer.a_error ? answer.q_invalid_answer_help_text : ""
              }
            />
          </Box>
        );
        break;
      case "currency":
        const uiValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string && answer.a_string.length > 0
              ? `$${formatMoney(answer.a_string, 0)}`
              : null
            : !isNaN(currentValues[answer.q_title]) &&
              currentValues[answer.q_title]
            ? `$${formatMoney(currentValues[answer.q_title], 0)}`
            : null;

        const valueChange = valueChanges[answer.q_title];
        const changeDisplay = valueChange ? (
          <Typography
            sx={{
              position: "absolute",
              right: theme.spacing(2),
              top: "50%",
              transform: "translateY(-50%)",
              color: valueChange > 0 ? "success.main" : "error.main",
              animation: "fadeOut 2s forwards",
              "@keyframes fadeOut": {
                "0%": { opacity: 1 },
                "70%": { opacity: 1 },
                "100%": { opacity: 0 },
              },
              zIndex: 1,
              pointerEvents: "none",
            }}
          >
            {valueChange > 0 ? "+" : "-"}$
            {formatMoney(Math.abs(valueChange), 0)}
          </Typography>
        ) : null;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(3),
              width: "100%",
              position: "relative",
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              helperText={answer.q_help_text ? answer.q_help_text : ""}
              sx={{
                width: "100%",
                "& .MuiInputBase-root": {
                  paddingRight: valueChange
                    ? theme.spacing(8)
                    : theme.spacing(2),
                },
              }}
              label={
                answer.a_error
                  ? "Error"
                  : answer.q_placeholder
                  ? answer.q_placeholder
                  : answer.q_title
              }
              type={answer.a_type_input}
              inputProps={{
                inputMode: "numeric",
              }}
              value={uiValue || ""}
              onChange={(e) => {
                const num = e.target.value.replace(/\D/g, "");
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = num;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  num,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  const next_q_id = answer.q_next_step_func(
                    answer,
                    answers,
                    questions
                  );
                  if (next_q_id) {
                    const nextQuestion = questions.find(
                      (q) => q.q_id === next_q_id
                    );
                    const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                    const nextQuestionTextField = document.getElementById(
                      nextQuestionTextFieldId
                    );
                    if (nextQuestionTextField) {
                      nextQuestionTextField.focus();
                    }
                  }
                }
              }}
            />
            {changeDisplay}
          </Box>
        );
        break;
      case "number":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(2),
              width: "100%",
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              helperText={
                answer.a_error
                  ? answer.q_invalid_answer_help_text
                  : answer.q_help_text
                  ? answer.q_help_text
                  : ""
              }
              error={answer.a_error}
              label={
                answer.a_error
                  ? "Error"
                  : answer.q_placeholder
                  ? answer.q_placeholder
                  : answer.q_title
              }
              type={answer.a_type_input}
              value={
                currentValues[answer.q_title] === undefined
                  ? answer.a_string
                    ? answer.a_string
                    : null
                  : currentValues[answer.q_title]
              }
              InputLabelProps={{
                shrink:
                  currentValues[answer.q_title] === undefined
                    ? answer.a_string
                      ? answer.a_string
                      : null
                    : currentValues[answer.q_title],
              }}
              onChange={(e) => {
                const value = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = value;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  value,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );

                // this is done to reset
                // 'get rate' output when new stuff is typed in
                setLoanQuoteData({});
              }}
            />
          </Box>
        );
        break;
      case "address":
        const address = calc?.Address;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
            sx={{ margin: theme.spacing(3, 0, 3, 0) }}
          >
            <AddressAutocomplete
              autocompleteOnKeyDown={(
                e,
                setSuggestionsVisible,
                setLocalAddress,
                place,
                locationList,
                setLocationList
              ) => {
                if (e.key === "Enter") {
                  setSuggestionChoice(place.formatted_address);

                  handleUserSelection(
                    place.formatted_address,
                    answer,
                    answers,
                    questions,
                    setAnswers
                  );
                }
              }}
              autocompleteOnClick={(
                e,
                setSuggestionsVisible,
                setLocalAddress,
                place,
                locationList,
                setLocationList
              ) => {
                setSuggestionChoice(place.formatted_address);

                handleUserSelection(
                  place.formatted_address,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              textFieldLabel={answer.a_error ? "Error" : answer.q_title}
              textFieldAriaLabel={"Enter address of the property for this loan"}
              searchOptionsTypes={[]}
              externalLocalAddress={address !== "" && address}
              error={answer.a_error}
            />
          </Box>
        );
        break;
      case "date":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(2),
            }}
          >
            <>
              <Box>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    views={["month", "year"]}
                    label={"Date"}
                    value={
                      currentValues[answer.q_title] === undefined
                        ? null
                        : dayjs(currentValues[answer.q_title])
                    }
                    onChange={(newDate) => {
                      const newDateString = newDate
                        ? newDate.format("YYYY-MM")
                        : "";

                      const updatedCurrentValues = Object.assign(
                        {},
                        currentValues
                      );
                      updatedCurrentValues[answer.q_title] = newDateString;

                      setCurrentValues(updatedCurrentValues);

                      handleUserSelection(
                        newDateString,
                        answer,
                        answers,
                        questions,
                        setAnswers
                      );
                    }}
                    onBlur={(newDate) => {
                      const newDateString = newDate
                        ? newDate.format("YYYY-MM") // it needs this input to read in // but we like to display mm / yyyy in freedomlandia
                        : "";
                      processSubmission(newDateString);
                    }}
                    format="MM/YYYY"
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </Box>
            </>
          </Box>
        );
        break;
      default:
    }

    return answerOptionsUI;
  };

  const showError =
    loanQuoteData?.LTVObj &&
    (Object.keys(loanQuoteData.LTVObj).length === 0 ||
      creditScore <
        loanQuoteData.LTVObj[Object.keys(loanQuoteData.LTVObj)[0]]
          .creditScoreCutoff);

  const resetCalculator = () => {
    // Loop through all answers and set a_string to ""
    const updatedAnswers = answers.map((answer) => ({
      ...answer,
      a_string: "",
    }));

    // Reset current values to ""
    const updatedCurrentValues = Object.keys(currentValues).reduce(
      (acc, key) => {
        acc[key] = ""; // Set each current value to an empty string
        return acc;
      },
      {}
    );

    setCurrentValues(updatedCurrentValues); // Update current values in state
  };

  const saveOrUpdate = async (isPDFGenerated = false) => {
    const targetUserID = userProfile.UID;

    let savedOrUpdated = "saved";
    const calcData = {}; // not used for escrow

    let updatedCalc;
    if (!calc) {
      updatedCalc = await createCalc(
        user,
        targetUserID,
        answers,
        calcType,
        calcData,
        true,
        isPDFGenerated
      );
      // send an email to the user
      // with their calc details
      // so they have a way to return to it
      // this is done on the backend now, if query param l=1 is passed to createCalc
    } else {
      savedOrUpdated = "updated";
      const name = answers?.filter((answer) => {
        return answer?.q_title === "Calculation name";
      })?.[0]?.a_string;

      const address = answers?.filter((answer) => {
        return answer?.q_title === "Address (optional)";
      })?.[0]?.a_string;

      updatedCalc = await updateCalc(user, calc, {
        Answers: answers,
        CalcData: calcData,
        Name: name,
        Address: address,
        PDFGenerated: isPDFGenerated,
      });
    }

    setCalc(updatedCalc);

    if (!showUpdateFields) {
      setShowCreatedSuccessMessage(true);
    }

    if (!isPDFGenerated) {
      // only show this if not concurently generating the pdf
      setSnackbarData({
        message: `Calculation ${savedOrUpdated}`,
        options: {
          variant: "success",
          autoHideDuration: 5000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        },
      });
    }

    gtag.event({ action: "escrow_calc-saved" });
  };

  const toggleAllDescriptions = () => {
    const newState = !showAllDescriptions;
    setShowAllDescriptions(newState);

    // Get all question titles from the answers, excluding "Closing cost"
    const allQuestionTitles = answers
      .map((answer) => answer.q_title)
      .filter((title) => title !== "Closing cost");

    // Create updated state with all questions set to the new state
    const updatedShowQuestionDescription = allQuestionTitles.reduce(
      (acc, title) => {
        acc[title] = newState;
        return acc;
      },
      { "Closing cost": true } // Always keep closing cost open
    );

    setShowQuestionDescription(updatedShowQuestionDescription);
  };

  return (
    <Box
      key={`calculation_${externalCalc?.id ? externalCalc.id : 0}`}
      sx={{
        display: "flex",
        flexDirection: "column",
        margin: theme.spacing(0, "auto"),
        width: "100%",
        maxWidth: "100%",
        position: "relative",
        paddingBottom: !isInView ? "200px" : 0,
      }}
    >
      {router.pathname.includes("/my/calculation") && calc && (
        <Box sx={{ width: "100%", display: "flex" }}>
          <IconButton
            sx={{
              margin: theme.spacing("auto", 0.5, "auto", "auto"),
            }}
            key={`icon_button_delete_calc_${calc.id}`}
            aria-label="loan settings"
          >
            <MoreVertIcon
              key={`vert_icon_delete_calc_${calc.id}`}
              aria-controls={
                Boolean(anchorEl[calc.id]) ? "basic-menu" : undefined
              }
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl[calc.id]) ? "true" : undefined}
              onClick={(e) => handleOpen(e, calc)}
            />
            <Menu
              key={`menu_delete_calc_${calc.id}`}
              anchorEl={anchorEl[calc.id]}
              open={Boolean(anchorEl[calc.id])}
              onClose={() => handleClose(calc)}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem key={`menu_item_delete_${calc.id}`}>
                <Button
                  sx={{ width: "100%" }}
                  variant="contained"
                  color="error"
                  onClick={() => {
                    (async () => {
                      await updateCalc(user, calc, {
                        Deleted: true,
                      });

                      handleClose(calc);

                      await sleep(1000); // BE deleted flag need a sec to return on next call
                      await loadCalcs(refCalcs);
                    })();
                  }}
                >
                  Delete calculation
                </Button>
              </MenuItem>
            </Menu>
          </IconButton>
        </Box>
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={resetCalculator}
          sx={{
            width: "25%",
            [theme.breakpoints.down("md")]: {
              width: "33%",
            },
            "@media print": { display: "none" },
          }}
          className="no-print-element"
        >
          Reset
        </Button>
        <IconButton
          onClick={toggleAllDescriptions}
          color="info"
          sx={{
            "@media print": { display: "none" },
          }}
          className="no-print-element"
        >
          {showAllDescriptions ? <KeyboardArrowUpIcon /> : <InfoOutlinedIcon />}
        </IconButton>
      </Box>

      {answers.length > 0 ? (
        <Box id="calculator-to-print">
          <Box id="pdf-1-input">
            <QuestionRenderer
              answers={answers}
              questions={firstPartOfQuestions}
              theme={theme}
              showQuestionDescription={showQuestionDescription}
              setShowQuestionDescription={setShowQuestionDescription}
              currentValues={currentValues}
              generateAnswerOptions={generateAnswerOptions}
              descriptionParams={{ loanTypeLineItemPresets, loanAmount }}
            />
          </Box>

          <Box id="pdf-4">
            <QuestionRenderer
              answers={answers}
              questions={thirdPartOfQuestions}
              theme={theme}
              showQuestionDescription={showQuestionDescription}
              setShowQuestionDescription={setShowQuestionDescription}
              currentValues={currentValues}
              generateAnswerOptions={generateAnswerOptions}
              calculatorType={calcType}
              descriptionParams={{ loanTypeLineItemPresets }}
            />
          </Box>
          <Box
            id="pdf-5"
            sx={{
              position: isInView ? "relative" : "sticky",
              minHeight: {
                xs: theme.spacing(18),
                md: "auto",
              },
              bottom: 0,
              width: "100%",
              backgroundColor: theme.palette.background.paper,
              borderTop: !isInView
                ? `1px solid ${theme.palette.grey[300]}`
                : "none",
              transition: "all 0.3s ease",
              zIndex: 2,
            }}
          >
            <Box
              sx={{
                padding: theme.spacing(2),
                [theme.breakpoints.down("md")]: {
                  padding: theme.spacing(2, 1),
                },
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    cursor: {
                      xs: isInView ? "pointer" : "default",
                      md: "pointer",
                    },
                  }}
                  onClick={() => {
                    let updatedShowQuestionDescription = Object.assign(
                      {},
                      showQuestionDescription
                    );
                    updatedShowQuestionDescription[`Closing cost`] =
                      !updatedShowQuestionDescription[`Closing cost`];
                    setShowQuestionDescription(updatedShowQuestionDescription);
                  }}
                >
                  <Box
                    sx={{
                      flex: "0 1 50%",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: theme.spacing(3),
                        [theme.breakpoints.down("md")]: {
                          fontSize: theme.spacing(2.5),
                        },
                      }}
                    >
                      Closing cost
                    </Typography>
                    {showQuestionDescription[`Closing cost`] ? (
                      <KeyboardArrowUpIcon
                        sx={{
                          ml: 1,
                          fontSize: theme.spacing(3),
                        }}
                      />
                    ) : (
                      <InfoOutlinedIcon
                        sx={{
                          ml: 1,
                          fontSize: theme.spacing(2.5),
                        }}
                        color="info"
                      />
                    )}
                  </Box>
                  <Box
                    sx={{
                      flex: "0 1 50%",
                      display: "flex",
                      justifyContent: "flex-end",
                      fontSize: theme.spacing(3),
                      fontWeight: 600,
                    }}
                  >
                    ${formatMoney(closingCost, 0)}
                  </Box>
                </Box>
                <Collapse
                  in={showQuestionDescription[`Closing cost`]}
                  timeout={300}
                >
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontSize: theme.spacing(1.8),
                        lineHeight: 2,
                      }}
                    >
                      {`$${formatMoney(
                        parseFloat(currentValues["Origination fee"] || 0),
                        0
                      )}`}{" "}
                      Origination fee
                      <br />+{" "}
                      {`$${formatMoney(
                        parseFloat(currentValues["Loan fees"] || 0),
                        0
                      )}`}{" "}
                      Loan fees
                      <br />+{" "}
                      {`$${formatMoney(
                        parseFloat(currentValues["Appraisal"] || 0),
                        0
                      )}`}{" "}
                      Appraisal
                      <br />+{" "}
                      {`$${formatMoney(
                        parseFloat(currentValues["Title fees"] || 0),
                        0
                      )}`}{" "}
                      Title fees
                      <br />+{" "}
                      {`$${formatMoney(
                        parseFloat(currentValues["Escrow"] || 0),
                        0
                      )}`}{" "}
                      Escrow
                      <br />+{" "}
                      {`$${formatMoney(
                        parseFloat(currentValues["Prepaid interest"] || 0),
                        0
                      )}`}{" "}
                      Prepaid interest
                      <br />+{" "}
                      {`$${formatMoney(
                        parseFloat(currentValues["Prepaid insurance"] || 0),
                        0
                      )}`}{" "}
                      Prepaid insurance
                      <br />={" "}
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        ${formatMoney(closingCost, 0)}
                      </Box>{" "}
                      Closing cost
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            </Box>
          </Box>

          <Box id="pdf-6-save-share-pdf-questions">
            <SaveSharePDF
              answers={answers}
              allowedQuestionTitles={fourthPart}
              generateAnswerOptions={generateAnswerOptions}
              calc={calc}
              setCalc={setCalc}
              saveOrUpdate={saveOrUpdate}
              calculatorType={calcType}
            />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{ margin: theme.spacing(2, 0), width: "100%", minHeigh: "100vh" }}
        >
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
        </Box>
      )}
      <Dialog
        open={showLogInDialog}
        onClose={() => setShowLogInDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Authenticator customTitle={"Log in or sign up to do that"} />
        </DialogContent>
      </Dialog>
      <Box id="closing-cost-anchor" />
    </Box>
  );
}
